import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { useMemberDirectory, useFamilyDirectory } from '../../hooks/useDirectory';
import { ageFrom } from '../../utils/format';
import type { Member, Family } from '../../types';

// "Which member is this about?" — the control that replaced the typed Member ID
// box in the registers.
//
// The registers used to store a member id the clerk typed from memory, so a
// slip made a certificate that pointed at nobody. Picking from the directory
// makes the link real, and lets the form fill in the name, date of birth and
// household by itself.
//
// Picking stays optional everywhere. A visitor's child is baptised and an
// outsider is married here; those entries are recorded by name alone, and a
// required picker would only teach clerks to invent a member.

/** How many rows the menu draws before asking for a narrower search. */
const MAX_VISIBLE = 40;

interface LookupOption {
  id: string;
  primary: string;
  secondary: string;
  haystack: string;
}

interface LookupFieldProps {
  label?: ReactNode;
  value?: string;
  onPick: (id: string) => void;
  options: LookupOption[];
  loading: boolean;
  placeholder: string;
  emptyMessage: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

/** The shared shell: type to filter, click to pick, ✕ to clear. */
const LookupField = ({
  label, value = '', onPick, options, loading, placeholder, emptyMessage,
  required = false, disabled = false, error, hint, fullWidth = false,
  containerStyle, isDarkMode
}: LookupFieldProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  const selected = useMemo(
    () => options.find((o) => o.id === value),
    [options, value]
  );

  const [text, setText] = useState('');
  const [typing, setTyping] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // While the menu is open the box holds what is being typed; the rest of the
  // time it shows the picked record, so the field reads as an answer.
  useEffect(() => {
    if (!typing) setText(selected ? selected.primary : '');
  }, [selected, typing]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTyping(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const term = typing ? text.trim().toLowerCase() : '';
  const matches = useMemo(
    () => (term ? options.filter((o) => o.haystack.includes(term)) : options),
    [options, term]
  );
  const visible = matches.slice(0, MAX_VISIBLE);

  const pick = (option: LookupOption) => {
    setTyping(false);
    setText(option.primary);
    setOpen(false);
    onPick(option.id);
  };

  const clear = () => {
    setTyping(false);
    setText('');
    setOpen(false);
    onPick('');
  };

  return (
    <div
      ref={wrapperRef}
      style={{ ...s.inputGroup, position: 'relative', ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}
    >
      {label && (
        <label style={s.label}>
          {label}
          {required && <span style={{ color: t.error, marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          value={text}
          disabled={disabled}
          placeholder={loading ? 'Loading…' : placeholder}
          onChange={(e) => { setTyping(true); setText(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          style={{
            ...s.input,
            paddingRight: value ? '38px' : undefined,
            ...(error ? { borderColor: t.error } : {}),
            ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {})
          }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={clear}
            title="Clear — record this entry by name only"
            style={{
              position: 'absolute', right: '8px', border: 'none', background: 'transparent',
              cursor: 'pointer', fontSize: t.fontSize.md, color: t.textFaint, lineHeight: 1, padding: '4px'
            }}
          >
            ✕
          </button>
        )}
      </div>

      {selected && !error && (
        <span style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '4px' }}>
          {selected.secondary}
        </span>
      )}
      {!selected && hint && !error && (
        <span style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '4px' }}>{hint}</span>
      )}
      {error && (
        <span style={{ fontSize: t.fontSize.sm, color: t.error, marginTop: '4px' }}>{error}</span>
      )}

      {open && !disabled && (
        <div style={s.dropdownMenu}>
          {visible.length > 0 ? (
            visible.map((option) => (
              <div
                key={option.id}
                style={{ ...s.dropdownItem, display: 'flex', flexDirection: 'column', gap: '2px' }}
                onMouseDown={() => pick(option)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.bgHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <strong>{option.primary}</strong>
                <span style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>{option.secondary}</span>
              </div>
            ))
          ) : (
            <div style={{ padding: '10px 14px', fontSize: t.fontSize.base, color: t.textFaint }}>
              {loading ? 'Loading…' : emptyMessage}
            </div>
          )}
          {matches.length > visible.length && (
            <div style={s.dropdownFooter}>
              {matches.length - visible.length} more — keep typing to narrow it down
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export interface MemberPickerProps {
  label?: ReactNode;
  /** The member id, e.g. `MEM-8041`. Empty means "not a member". */
  value?: string;
  /** Receives the id and the whole record, so the form can fill itself in. */
  onChange: (memberId: string, member: Member | null) => void;
  /** Narrows the list — one gender for a groom/bride field, one family, etc. */
  filter?: (member: Member) => boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

const MemberPicker = ({
  label = 'Member', value = '', onChange, filter,
  placeholder = 'Search by name, member ID or family…',
  hint = 'Leave blank if the person is not on the church rolls.',
  ...rest
}: MemberPickerProps) => {
  const { members, loading } = useMemberDirectory();

  const pool = useMemo(
    () => (filter ? members.filter(filter) : members),
    [members, filter]
  );

  const options = useMemo<LookupOption[]>(
    () => pool
      .filter((m) => m.memberId)
      .map((m) => {
        const age = ageFrom(m.dateOfBirth, '');
        const secondary = [m.memberId, m.familyId, m.relationToHead, age ? `${age}y` : '']
          .filter(Boolean).join('  ·  ');
        return {
          id: String(m.memberId),
          primary: m.name || String(m.memberId),
          secondary,
          haystack: [m.name, m.memberId, m.familyId, m.relativeName, m.mobileNumber]
            .filter(Boolean).join(' ').toLowerCase()
        };
      }),
    [pool]
  );

  const byId = useMemo(() => {
    const map = new Map<string, Member>();
    pool.forEach((m) => { if (m.memberId) map.set(m.memberId, m); });
    return map;
  }, [pool]);

  return (
    <LookupField
      {...rest}
      label={label}
      value={value}
      options={options}
      loading={loading}
      placeholder={placeholder}
      hint={hint}
      emptyMessage="No member matches. Clear the box to record this entry by name only."
      onPick={(id) => onChange(id, id ? byId.get(id) || null : null)}
    />
  );
};

export interface FamilyPickerProps {
  label?: ReactNode;
  /** The family id, e.g. `FAM-12`. */
  value?: string;
  onChange: (familyId: string, family: Family | null) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

/** Picks a household by its head — how a parish actually refers to a family. */
export const FamilyPicker = ({
  label = 'Family', value = '', onChange,
  placeholder = 'Search by head of family, family ID or place…',
  hint = 'The household the child belongs to.',
  ...rest
}: FamilyPickerProps) => {
  const { families, loading } = useFamilyDirectory();

  const options = useMemo<LookupOption[]>(
    () => families
      .filter((f) => f.familyId)
      .map((f) => ({
        id: String(f.familyId),
        primary: f.headOfFamily || f.familyName || String(f.familyId),
        secondary: [f.familyId, f.parish, f.contact || f.contactNumber]
          .filter(Boolean).join('  ·  '),
        haystack: [f.headOfFamily, f.familyName, f.familyId, f.membershipNo, f.parish]
          .filter(Boolean).join(' ').toLowerCase()
      })),
    [families]
  );

  const byId = useMemo(() => {
    const map = new Map<string, Family>();
    families.forEach((f) => { if (f.familyId) map.set(f.familyId, f); });
    return map;
  }, [families]);

  return (
    <LookupField
      {...rest}
      label={label}
      value={value}
      options={options}
      loading={loading}
      placeholder={placeholder}
      hint={hint}
      emptyMessage="No family matches."
      onPick={(id) => onChange(id, id ? byId.get(id) || null : null)}
    />
  );
};

export default MemberPicker;
